import {
  AlertTriangle,
  Bot,
  Camera,
  Check,
  Clipboard,
  ExternalLink,
  ImagePlus,
  LoaderCircle,
  Share2,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  type ChangeEvent,
  type RefObject,
  useMemo,
  useRef,
  useState,
} from "react";
import { CATEGORIES } from "../types";
import { ApiError, api } from "../lib/api";
import {
  buildWinePhotoAnalysisPrompt,
  parseWinePhotoAnalysisResponse,
  type WineAiAnalysisResult,
  type WineAiField,
  type WineAiSuggestion,
  type WinePhotoAiValues,
} from "../lib/wine-photo-ai";
import { copyText } from "../lib/clipboard";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

interface PhotoSelection {
  file: File;
  preview: string;
}

interface ReviewSuggestion extends WineAiSuggestion {
  selected: boolean;
  edited: boolean;
}

interface WinePhotoImportModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: WinePhotoAiValues) => void;
}

const FIELD_ORDER: WineAiField[] = [
  "producteur",
  "nom",
  "appellation",
  "region",
  "millesime",
  "categorie",
  "format",
  "cepages",
  "vinification_elevage",
];

const FIELD_LABELS: Record<WineAiField, string> = {
  producteur: "Producteur / domaine",
  nom: "Nom / cuvée",
  appellation: "Appellation",
  region: "Région / pays",
  cepages: "Cépage(s)",
  vinification_elevage: "Vinification et élevage",
  millesime: "Millésime",
  categorie: "Catégorie",
  format: "Format",
};

const ORIGIN_LABELS: Record<WineAiSuggestion["origin"], string> = {
  etiquette: "Lu sur l’étiquette",
};

function filePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("Aperçu indisponible."));
    reader.onerror = () => reject(new Error("Cette photo ne peut pas être lue."));
    reader.readAsDataURL(file);
  });
}

function canShareFiles(files: File[]): boolean {
  if (typeof navigator.share !== "function") return false;
  if (typeof navigator.canShare !== "function") return true;
  try {
    return navigator.canShare({ files });
  } catch {
    return false;
  }
}

function uniqueText(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function reviewSuggestions(result: WineAiAnalysisResult): ReviewSuggestion[] {
  const byField = new Map<WineAiField, ReviewSuggestion>();
  for (const suggestion of result.suggestions) {
    if (byField.has(suggestion.field)) continue;
    byField.set(suggestion.field, {
      ...suggestion,
      selected: suggestion.confidence !== "faible",
      edited: false,
    });
  }
  return FIELD_ORDER.flatMap((field) => {
    const suggestion = byField.get(field);
    return suggestion ? [suggestion] : [];
  });
}

function manualSuggestions(values: WinePhotoAiValues): ReviewSuggestion[] {
  return FIELD_ORDER.flatMap((field) => {
    if (!Object.prototype.hasOwnProperty.call(values, field)) return [];
    const value = values[field];
    if (value === undefined) return [];
    return [{
      field,
      value,
      confidence: "moyenne" as const,
      origin: "etiquette" as const,
      evidence: "Réponse importée manuellement : vérifiez cette information.",
      selected: true,
      edited: false,
    }];
  });
}

function valuesFromReview(review: readonly ReviewSuggestion[]): WinePhotoAiValues {
  const values: Partial<Record<WineAiField, string | number | null>> = {};
  for (const suggestion of review) {
    if (!suggestion.selected) continue;
    if (suggestion.value === null) {
      values[suggestion.field] = null;
      continue;
    }
    if (suggestion.field === "millesime") {
      const parsed = Number(suggestion.value);
      if (Number.isInteger(parsed) && parsed >= 1000 && parsed <= 9999) {
        values[suggestion.field] = parsed;
      }
      continue;
    }
    const value = String(suggestion.value).trim();
    if (value) values[suggestion.field] = value;
  }
  return values as WinePhotoAiValues;
}

function aiErrorMessage(error: unknown): string {
  const retryOrFallback = "Réessayez ou utilisez le mode manuel.";
  if (error instanceof ApiError) {
    if (error.code === "IA_NON_CONFIGUREE") {
      return "L’analyse intégrée n’est pas encore configurée. Vous pouvez continuer avec une autre IA dans le mode manuel ci-dessous.";
    }
    if (error.code === "IA_ACCES_REFUSE") {
      return "La clé du service d’analyse a été refusée. Vous pouvez continuer avec une autre IA dans le mode manuel ci-dessous.";
    }
    if (error.code === "REQUETE_IA_INVALIDE") {
      return "Le format envoyé au service d’analyse n’est pas accepté. Vous pouvez continuer avec une autre IA dans le mode manuel ci-dessous.";
    }
    if (error.code === "IA_QUOTA_DEPASSEE") {
      return `Le quota d’analyse est momentanément atteint. ${retryOrFallback}`;
    }
    if (error.code === "IA_DELAI_DEPASSE") {
      return `L’IA met trop de temps à répondre. ${retryOrFallback}`;
    }
    if (error.code === "IA_INDISPONIBLE") {
      return `L’IA est momentanément indisponible. ${retryOrFallback}`;
    }
    if (error.code === "REPONSE_IA_INVALIDE") {
      return "L’IA n’a pas fourni d’informations suffisamment fiables. Essayez une photo plus nette.";
    }
    return error.message;
  }
  return error instanceof Error
    ? error.message
    : "L’analyse n’a pas abouti.";
}

function opensManualFallback(error: unknown): boolean {
  return !(error instanceof ApiError) || [
    "IA_NON_CONFIGUREE",
    "IA_ACCES_REFUSE",
    "REQUETE_IA_INVALIDE",
    "IA_QUOTA_DEPASSEE",
    "IA_DELAI_DEPASSE",
    "IA_INDISPONIBLE",
  ].includes(error.code ?? "");
}

export function WinePhotoImportModal({
  open,
  onClose,
  onApply,
}: WinePhotoImportModalProps) {
  const { showToast } = useToast();
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const operationRef = useRef(0);
  const sessionRef = useRef(0);
  const photoOperationRef = useRef({ front: 0, back: 0 });
  const [front, setFront] = useState<PhotoSelection | null>(null);
  const [back, setBack] = useState<PhotoSelection | null>(null);
  const [response, setResponse] = useState("");
  const [review, setReview] = useState<ReviewSuggestion[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [busy, setBusy] = useState<"analyse" | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);

  const files = useMemo(
    () => [front?.file, back?.file].filter((file): file is File => Boolean(file)),
    [back, front],
  );
  const prompt = front
    ? buildWinePhotoAnalysisPrompt(back ? 2 : 1)
    : "";
  const shareAvailable = front ? canShareFiles(files) : false;
  const selectedValues = useMemo(() => valuesFromReview(review), [review]);
  const selectedCount = Object.keys(selectedValues).length;
  const clearReview = () => {
    setReview([]);
    setWarnings([]);
    setAnalysisCompleted(false);
  };

  const reset = () => {
    operationRef.current += 1;
    sessionRef.current += 1;
    photoOperationRef.current.front += 1;
    photoOperationRef.current.back += 1;
    setFront(null);
    setBack(null);
    setResponse("");
    clearReview();
    setShareMessage("");
    setError("");
    setBusy(null);
    setManualOpen(false);
    if (frontInputRef.current) frontInputRef.current.value = "";
    if (backInputRef.current) backInputRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose();
  };

  const selectPhoto = async (
    side: "front" | "back",
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const session = sessionRef.current;
    const photoOperation = ++photoOperationRef.current[side];
    const file = event.target.files?.[0];
    if (!file) return;
    // Permet de sélectionner à nouveau la même photo, même après un remplacement.
    event.target.value = "";
    setError("");
    setShareMessage("");
    setResponse("");
    clearReview();

    if (!ACCEPTED_PHOTO_TYPES.has(file.type.toLocaleLowerCase("en"))) {
      setError("Utilisez une photo JPEG, PNG, WebP, HEIC ou HEIF.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError("Cette photo dépasse 8 Mo. Choisissez une image plus légère.");
      event.target.value = "";
      return;
    }

    try {
      const selection = { file, preview: await filePreview(file) };
      if (
        session !== sessionRef.current ||
        photoOperation !== photoOperationRef.current[side]
      ) return;
      if (side === "front") setFront(selection);
      else setBack(selection);
    } catch (caught) {
      if (
        session !== sessionRef.current ||
        photoOperation !== photoOperationRef.current[side]
      ) return;
      setError(
        caught instanceof Error
          ? caught.message
          : "Cette photo ne peut pas être utilisée.",
      );
      event.target.value = "";
    }
  };

  const removePhoto = (side: "front" | "back") => {
    operationRef.current += 1;
    photoOperationRef.current[side] += 1;
    if (side === "front") {
      setFront(null);
      if (frontInputRef.current) frontInputRef.current.value = "";
    } else {
      setBack(null);
      if (backInputRef.current) backInputRef.current.value = "";
    }
    setError("");
    setShareMessage("");
    setResponse("");
    clearReview();
  };

  const analyze = async () => {
    if (!front || busy) return;
    const operation = ++operationRef.current;
    setBusy("analyse");
    setError("");
    setWarnings([]);
    setAnalysisCompleted(false);
    try {
      const analysis = await api.analyzeWinePhotos(files);
      if (operation !== operationRef.current) return;
      setReview(reviewSuggestions(analysis));
      setWarnings(uniqueText(analysis.warnings));
      setAnalysisCompleted(true);
      if (!analysis.suggestions.length) setManualOpen(true);
    } catch (caught) {
      if (operation !== operationRef.current) return;
      setError(aiErrorMessage(caught));
      if (opensManualFallback(caught)) setManualOpen(true);
    } finally {
      if (operation === operationRef.current) setBusy(null);
    }
  };

  const copyPrompt = async () => {
    if (!prompt) return;
    try {
      await copyText(prompt);
      setError("");
      showToast({ message: "Prompt copié. Joignez maintenant les photos dans votre IA." });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "La copie a échoué.");
    }
  };

  const share = async () => {
    if (!front || !prompt) return;
    if (!shareAvailable) {
      setError(
        "Le partage de photos n’est pas disponible ici. Copiez le prompt puis ouvrez ChatGPT ou Gemini.",
      );
      return;
    }

    setError("");
    setShareMessage("");
    try {
      await navigator.share({
        title: "Identifier une bouteille pour Ma Cave",
        text: prompt,
        files,
      });
      setShareMessage(
        "Partage effectué. Au retour, collez ci-dessous la réponse JSON de l’IA.",
      );
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(
        "Le partage n’a pas abouti. Vous pouvez copier le prompt et joindre les photos manuellement.",
      );
    }
  };

  const inspectResponse = () => {
    const parsed = parseWinePhotoAnalysisResponse(response);
    if (!parsed.parsed || !Object.keys(parsed.values).length) {
      clearReview();
      setError(parsed.warnings[0] ?? "Aucune information exploitable n’a été trouvée.");
      return;
    }
    setReview(manualSuggestions(parsed.values));
    setWarnings(uniqueText(parsed.warnings));
    setError("");
  };

  const updateSuggestion = (
    field: WineAiField,
    changes: Partial<Pick<ReviewSuggestion, "selected" | "value">>,
  ) => {
    setReview((current) => current.map((suggestion) =>
      suggestion.field === field
        ? {
            ...suggestion,
            ...changes,
            edited: Object.prototype.hasOwnProperty.call(changes, "value")
              ? true
              : suggestion.edited,
          }
        : suggestion,
    ));
  };

  const apply = () => {
    if (!selectedCount) return;
    onApply(selectedValues);
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Ajouter depuis une photo"
      description="Analysez les étiquettes, puis choisissez et corrigez les informations avant de les ajouter à la fiche."
      size="large"
    >
      <div className="wine-photo-flow">
        <section className="wine-photo-step" aria-labelledby="wine-photo-step-one">
          <div className="wine-photo-step-heading">
            <span>1</span>
            <div>
              <h3 id="wine-photo-step-one">Ajoutez les photos de la bouteille</h3>
              <p>La face avant est nécessaire. Le dos améliore souvent le résultat.</p>
            </div>
          </div>

          <div className="wine-photo-grid">
            <PhotoPicker
              label="Étiquette avant"
              help="Obligatoire"
              selection={front}
              inputRef={frontInputRef}
              disabled={busy !== null}
              onChange={(event) => void selectPhoto("front", event)}
              onRemove={() => removePhoto("front")}
            />
            <PhotoPicker
              label="Contre-étiquette"
              help="Facultative"
              selection={back}
              inputRef={backInputRef}
              disabled={busy !== null}
              onChange={(event) => void selectPhoto("back", event)}
              onRemove={() => removePhoto("back")}
            />
          </div>
        </section>

        <section
          className="wine-photo-step"
          aria-labelledby="wine-photo-step-two"
          aria-busy={busy === "analyse"}
        >
          <div className="wine-photo-step-heading">
            <span>2</span>
            <div>
              <h3 id="wine-photo-step-two">Analysez les étiquettes</h3>
              <p>Gemini relève uniquement ce qui est lisible sur les photos.</p>
            </div>
          </div>

          <div className="privacy-note">
            <Bot size={20} aria-hidden="true" />
            <p>
              <strong>Vous gardez la main.</strong> Les photos sont transmises à
              Gemini uniquement lorsque vous lancez l’analyse. Cave ne les enregistre
              pas et aucune proposition n’est appliquée sans votre accord.
            </p>
          </div>

          {front ? (
            <button
              className="button button-primary wine-photo-analyze-button"
              type="button"
              disabled={busy !== null}
              onClick={() => void analyze()}
            >
              {busy === "analyse" ? (
                <><LoaderCircle className="spin" size={18} aria-hidden="true" /> Analyse en cours…</>
              ) : (
                <><Sparkles size={18} aria-hidden="true" /> {review.length ? "Relancer l’analyse" : "Analyser les étiquettes"}</>
              )}
            </button>
          ) : (
            <div className="wine-photo-empty-prompt">
              <ImagePlus size={21} aria-hidden="true" />
              <p>Ajoutez d’abord la photo de l’étiquette avant.</p>
            </div>
          )}
          {busy === "analyse" ? <p className="sr-only" role="status">Analyse des étiquettes en cours.</p> : null}

          <details
            className="wine-photo-manual"
            open={manualOpen}
            onToggle={(event) => setManualOpen(event.currentTarget.open)}
          >
            <summary>Utiliser une autre IA manuellement</summary>
            <div className="wine-photo-manual-body">
              <p className="wine-photo-share-help">
                Copiez le prompt, joignez les photos dans ChatGPT ou Gemini, puis
                collez ici la réponse JSON.
              </p>
              {front ? (
                <>
                  <details className="wine-photo-prompt">
                    <summary>Voir le prompt préparé</summary>
                    <pre className="prompt-preview">{prompt}</pre>
                  </details>
                  <div className="wine-photo-share-actions">
                    <button
                      className="button button-secondary"
                      type="button"
                      onClick={() => void share()}
                      disabled={!shareAvailable || busy !== null}
                    >
                      <Share2 size={18} aria-hidden="true" /> Partager photo + prompt
                    </button>
                    <button
                      className="button button-secondary"
                      type="button"
                      onClick={() => void copyPrompt()}
                      disabled={busy !== null}
                    >
                      <Clipboard size={18} aria-hidden="true" /> Copier le prompt
                    </button>
                  </div>
                  {!shareAvailable ? (
                    <p className="wine-photo-share-help">
                      Le partage direct n’est pas proposé par ce navigateur. Copiez le
                      prompt, ouvrez une IA et joignez les photos.
                    </p>
                  ) : null}
                  <div className="wine-photo-ai-links" aria-label="Ouvrir une intelligence artificielle">
                    <a className="button button-ghost" href="https://chatgpt.com/" target="_blank" rel="noreferrer">
                      Ouvrir ChatGPT <ExternalLink size={15} aria-hidden="true" />
                    </a>
                    <a className="button button-ghost" href="https://gemini.google.com/app" target="_blank" rel="noreferrer">
                      Ouvrir Gemini <ExternalLink size={15} aria-hidden="true" />
                    </a>
                  </div>
                  <label className="field wine-photo-response-field">
                    <span>Réponse JSON de l’IA</span>
                    <textarea
                      rows={7}
                      value={response}
                      onChange={(event) => {
                        setResponse(event.target.value);
                        clearReview();
                        setError("");
                      }}
                      placeholder={'Collez ici la réponse commençant par { "vin": … }'}
                    />
                  </label>
                  <button
                    className="button button-secondary wine-photo-inspect-button"
                    type="button"
                    disabled={!response.trim() || busy !== null}
                    onClick={inspectResponse}
                  >
                    <Sparkles size={17} aria-hidden="true" /> Vérifier la réponse
                  </button>
                </>
              ) : null}
              {shareMessage ? <p className="wine-photo-success" role="status"><Check size={17} aria-hidden="true" /> {shareMessage}</p> : null}
            </div>
          </details>
        </section>

        {review.length ? (
          <section
            className="wine-photo-step"
            aria-labelledby="wine-photo-step-three"
          >
            <div className="wine-photo-step-heading">
              <span>3</span>
              <div>
                <h3 id="wine-photo-step-three">Choisissez les informations à garder</h3>
                <p>Décochez une proposition ou corrigez-la directement avant de continuer.</p>
              </div>
            </div>

            <fieldset className="wine-photo-review" disabled={busy !== null}>
              <legend className="sr-only">Informations proposées</legend>
              <div className="wine-photo-review-heading">
                <Check size={18} aria-hidden="true" />
                <div>
                  <strong>{review.length} information{review.length > 1 ? "s" : ""} proposée{review.length > 1 ? "s" : ""}</strong>
                  <small>{selectedCount} sélectionnée{selectedCount > 1 ? "s" : ""} pour la fiche.</small>
                </div>
              </div>
              <div className="wine-photo-suggestions">
                {review.map((suggestion) => (
                  <SuggestionEditor
                    key={suggestion.field}
                    suggestion={suggestion}
                    onChange={(changes) => updateSuggestion(suggestion.field, changes)}
                  />
                ))}
              </div>
              {warnings.length ? (
                <div className="wine-photo-warnings">
                  <AlertTriangle size={17} aria-hidden="true" />
                  <ul>{warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}</ul>
                </div>
              ) : null}
              <p className="wine-photo-completion-help">
                Pour rechercher des informations complémentaires sur Internet,
                préremplissez d’abord la fiche puis utilisez « Compléter avec l’IA ».
              </p>
            </fieldset>
          </section>
        ) : null}

        {analysisCompleted && !review.length ? (
          <div className="wine-photo-warnings wine-photo-empty-result" role="status">
            <AlertTriangle size={17} aria-hidden="true" />
            <div>
              <strong>Aucune information exploitable n’a été détectée.</strong>
              {warnings.length ? (
                <ul>{warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}</ul>
              ) : (
                <p>Essayez une photo plus nette ou utilisez le mode manuel.</p>
              )}
            </div>
          </div>
        ) : null}

        {error ? <p className="form-error" role="alert">{error}</p> : null}

        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={close}>Fermer</button>
          <button
            className="button button-primary"
            type="button"
            disabled={!selectedCount || busy !== null}
            onClick={apply}
          >
            <Check size={18} aria-hidden="true" /> {selectedCount
              ? `Préremplir ${selectedCount} information${selectedCount > 1 ? "s" : ""}`
              : "Préremplir la fiche"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SuggestionEditor({
  suggestion,
  onChange,
}: {
  suggestion: ReviewSuggestion;
  onChange: (changes: Partial<Pick<ReviewSuggestion, "selected" | "value">>) => void;
}) {
  const label = FIELD_LABELS[suggestion.field];
  const inputId = `wine-photo-suggestion-${suggestion.field}`;
  const inputValue = suggestion.value ?? "";
  const longValue = suggestion.field === "vinification_elevage";
  const yearValue = ["millesime", "debut_deg", "fin_deg"].includes(suggestion.field);

  return (
    <div className={`wine-photo-suggestion ${suggestion.selected ? "is-selected" : ""} ${longValue ? "is-wide" : ""}`}>
      <label className="wine-photo-suggestion-choice">
        <input
          type="checkbox"
          checked={suggestion.selected}
          onChange={(event) => onChange({ selected: event.target.checked })}
          aria-label={`Garder ${label}`}
        />
        <span aria-hidden="true"><Check size={14} /></span>
      </label>
      <div className="wine-photo-suggestion-content">
        <div className="wine-photo-suggestion-heading">
          <label htmlFor={inputId}>{label}</label>
          {suggestion.edited ? (
            <span className="wine-photo-origin">Corrigé par vous</span>
          ) : (
            <>
              <span className={`wine-photo-origin origin-${suggestion.origin}`}>{ORIGIN_LABELS[suggestion.origin]}</span>
              <span className="wine-photo-confidence">Confiance {suggestion.confidence}</span>
            </>
          )}
        </div>
        {suggestion.field === "categorie" ? (
          <select
            id={inputId}
            value={String(inputValue)}
            onChange={(event) => onChange({ value: event.target.value })}
          >
            {CATEGORIES.map((category) => <option key={category}>{category}</option>)}
          </select>
        ) : longValue ? (
          <textarea
            id={inputId}
            rows={3}
            value={String(inputValue)}
            onChange={(event) => onChange({ value: event.target.value })}
          />
        ) : (
          <input
            id={inputId}
            type={yearValue ? "number" : "text"}
            min={yearValue ? 1000 : undefined}
            max={yearValue ? 9999 : undefined}
            inputMode={yearValue ? "numeric" : undefined}
            disabled={suggestion.field === "millesime" && suggestion.value === null}
            value={inputValue}
            onChange={(event) => onChange({
              value: yearValue && event.target.value
                ? Number(event.target.value)
                : event.target.value,
            })}
          />
        )}
        {suggestion.field === "millesime" ? (
          <label className="check-row wine-photo-non-vintage">
            <input
              type="checkbox"
              checked={suggestion.value === null}
              onChange={(event) => onChange({ value: event.target.checked ? null : "" })}
            />
            <span><Check size={14} aria-hidden="true" /> Non millésimé</span>
          </label>
        ) : null}
        {!suggestion.edited && suggestion.evidence ? <small className="wine-photo-evidence">{suggestion.evidence}</small> : null}
      </div>
    </div>
  );
}

function PhotoPicker({
  label,
  help,
  selection,
  inputRef,
  disabled,
  onChange,
  onRemove,
}: {
  label: string;
  help: string;
  selection: PhotoSelection | null;
  inputRef: RefObject<HTMLInputElement | null>;
  disabled: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}) {
  return (
    <div className={`wine-photo-picker ${selection ? "has-photo" : ""}`}>
      {selection ? (
        <img src={selection.preview} alt={`Aperçu — ${label.toLocaleLowerCase("fr")}`} />
      ) : (
        <div className="wine-photo-placeholder" aria-hidden="true"><Camera size={27} /></div>
      )}
      <div className="wine-photo-picker-body">
        <div><strong>{label}</strong><small>{help}</small></div>
        <div className="wine-photo-picker-actions">
          <label className={`button button-secondary ${disabled ? "is-disabled" : ""}`}>
            <Camera size={16} aria-hidden="true" /> <span>Prendre une photo</span>
            <input
              ref={inputRef}
              className="sr-only"
              type="file"
              accept="image/*"
              capture="environment"
              aria-label={label}
              disabled={disabled}
              onChange={onChange}
            />
          </label>
          <label className={`button button-secondary ${disabled ? "is-disabled" : ""}`}>
            <ImagePlus size={16} aria-hidden="true" /> <span>Choisir dans mes photos</span>
            <input
              className="sr-only"
              type="file"
              accept="image/*"
              aria-label={`${label} — Choisir dans mes photos`}
              disabled={disabled}
              onChange={onChange}
            />
          </label>
        </div>
        {selection ? (
          <button className="wine-photo-remove" type="button" disabled={disabled} onClick={onRemove}>
            <Trash2 size={15} aria-hidden="true" /> Retirer
          </button>
        ) : null}
      </div>
    </div>
  );
}
